import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export function HomeHCASection() {
  return (
    <section className="relative overflow-hidden" data-testid="hca-homepage-section">
      {/* Distinct navy band — clearly different from homepage slate */}
      <div className="relative bg-[#07182D]">
        {/* Top accent line */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#169BD5]/50 to-transparent" />

        {/* Faint radial glow behind the section for depth */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 60% at 70% 50%, rgba(22,155,213,0.06) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">

            {/* LEFT — Text column */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span
                className="text-[11px] tracking-[0.25em] text-[#169BD5] uppercase mb-5 block font-semibold"
                data-testid="hca-section-label"
              >
                An Emerging Field of Study
              </span>

              <h2
                className="text-2xl sm:text-3xl md:text-[2.25rem] leading-[1.25] text-white mb-5"
                data-testid="hca-section-headline"
              >
                <span className="font-light">As technology becomes more capable,</span>
                <br />
                <span className="font-bold">what happens to the human role?</span>
              </h2>

              <p className="text-xl md:text-2xl text-[#169BD5] font-semibold italic mb-7">
                What about me?
              </p>

              <p className="text-[15px] text-slate-300 leading-[1.7] mb-6 max-w-lg">
                Human Continuity Architecture (HCA) is an emerging field of study examining how human agency, judgment, participation, accountability, knowledge, labor, memory, and continuity change as capabilities once performed by people are increasingly shared with or transferred to technological systems.
              </p>

              <div className="mb-9">
                <p className="text-sm text-slate-400">Not anti-AI.&nbsp; Not anti-progress.</p>
                <p className="text-lg text-white font-semibold">A more deliberate future.</p>
              </div>

              <Link
                to="/hca"
                className="group inline-flex items-center gap-2.5 text-xs tracking-[0.14em] uppercase text-[#169BD5] hover:text-white transition-colors font-bold"
                data-testid="hca-section-cta"
              >
                Explore Human Continuity Architecture
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>

            {/* RIGHT — Visual column */}
            <motion.div
              className="relative flex flex-col items-center justify-center"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              {/* Subtle infinity watermark — very background */}
              <img
                src="/assets/hca-logo-icon.png"
                alt=""
                aria-hidden="true"
                className="absolute w-[480px] max-w-[110%] opacity-[0.04] select-none pointer-events-none"
                style={{ objectFit: 'contain' }}
              />

              {/* Official HCA Business Header — white panel container */}
              <div
                className="relative z-10 w-full max-w-md lg:max-w-[460px] bg-white rounded-xl p-8 md:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.35)]"
                data-testid="hca-section-logo-container"
              >
                <img
                  src="/assets/hca-business-header.png"
                  alt="Human Continuity Architecture"
                  className="w-full h-auto"
                  style={{ objectFit: 'contain' }}
                  data-testid="hca-section-logo"
                />
              </div>

              {/* Four pillars beneath the logo card */}
              <div className="mt-6 flex items-center justify-center gap-3 text-[11px] text-slate-400 tracking-[0.12em] uppercase">
                <span>People</span>
                <span className="w-px h-3 bg-slate-600" />
                <span>Technology</span>
                <span className="w-px h-3 bg-slate-600" />
                <span>Choices</span>
                <span className="w-px h-3 bg-slate-600" />
                <span>Tomorrow</span>
              </div>
            </motion.div>

          </div>
        </div>

        {/* Bottom accent line */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#169BD5]/50 to-transparent" />
      </div>
    </section>
  );
}
